/**
 * Request History Manager Hook
 *
 * Custom React hook for managing in-memory request/response history.
 * Maintains a maximum of 50 entries with newest entries first.
 *
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5, 10.6
 */

import { useState, useCallback } from 'react';
import type { ApiTestRequest, ApiTestResponse, RequestHistoryEntry } from '../types';

/**
 * Maximum number of history entries to maintain
 */
const MAX_HISTORY_SIZE = 50;

/**
 * Hook return type
 */
export interface UseRequestHistoryReturn {
  /** Array of history entries (newest first) */
  history: RequestHistoryEntry[];
  /** Add a new history entry */
  addHistoryEntry: (request: ApiTestRequest, response: ApiTestResponse) => void;
  /** Clear all history */
  clearHistory: () => void;
}

/**
 * Custom hook for managing request/response history
 *
 * @returns History state and management functions
 *
 * @example
 * ```tsx
 * const { history, addHistoryEntry, clearHistory } = useRequestHistory();
 *
 * // After sending a request
 * addHistoryEntry(request, response);
 *
 * // Display history
 * history.map(entry => (
 *   <div key={entry.id}>
 *     {entry.timestamp} - {entry.request.endpoint.method} {entry.request.endpoint.path}
 *   </div>
 * ))
 * ```
 */
export function useRequestHistory(): UseRequestHistoryReturn {
  // Store history in component state (in-memory only)
  const [history, setHistory] = useState<RequestHistoryEntry[]>([]);

  /**
   * Add a new history entry
   *
   * Creates a history entry with:
   * - Unique ID generated by crypto.randomUUID()
   * - Timestamp in ISO 8601 format
   * - Complete request and response objects
   *
   * The entry is prepended to the beginning of the list.
   * If history exceeds 50 entries, oldest entries are removed.
   *
   * Requirement 10.1: Create history entry with unique ID (crypto.randomUUID())
   * Requirement 10.2: Include timestamp in ISO 8601 format
   * Requirement 10.3: Store complete request and response objects
   * Requirement 10.4: Prepend new entries to beginning of list
   * Requirement 10.5: Limit history to maximum 50 entries (remove oldest)
   */
  const addHistoryEntry = useCallback((request: ApiTestRequest, response: ApiTestResponse) => {
    const entry: RequestHistoryEntry = {
      id: crypto.randomUUID(), // Requirement 10.1: Unique ID
      timestamp: new Date().toISOString(), // Requirement 10.2: ISO 8601 timestamp
      request, // Requirement 10.3: Complete request object
      response, // Requirement 10.3: Complete response object
    };

    setHistory((prevHistory) => {
      // Requirement 10.4: Prepend to beginning of list
      const newHistory = [entry, ...prevHistory];

      // Requirement 10.5: Limit to maximum 50 entries
      if (newHistory.length > MAX_HISTORY_SIZE) {
        return newHistory.slice(0, MAX_HISTORY_SIZE);
      }

      return newHistory;
    });
  }, []);

  /**
   * Clear all history entries
   */
  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  return {
    history,
    addHistoryEntry,
    clearHistory,
  };
}
